import { haversineKm } from "./distance";
import { applyStaticTranslations, i18n, type MessageKey } from "./i18n";
import { stops } from "./stops";
import { estimateMinutes, formatDuration } from "./travel-time";
import { TRAVEL_MODE_ICON, type Stop, type TravelMode } from "./types";

type SummaryElements = {
  dialog: HTMLDialogElement;
  pre: HTMLPreElement;
  copyBtn: HTMLButtonElement;
  closeBtn: HTMLButtonElement;
  closeX: HTMLButtonElement;
};

const TRAVEL_LABEL_KEY: Record<TravelMode, MessageKey> = {
  plane: "travelPlane",
  train: "travelTrain",
  bus: "travelBus",
  car: "travelCar",
};

let els: SummaryElements | null = null;
let copyResetTimer: ReturnType<typeof setTimeout> | null = null;

export function initSummaryDialog(): void {
  const dialog = required<HTMLDialogElement>("#summary-dialog");
  els = {
    dialog,
    pre: required<HTMLPreElement>("#summary-text"),
    copyBtn: required<HTMLButtonElement>("#summary-copy"),
    closeBtn: required<HTMLButtonElement>("#summary-close"),
    closeX: required<HTMLButtonElement>("#summary-close-x"),
  };

  els.copyBtn.addEventListener("click", () => copyToClipboard());
  els.closeBtn.addEventListener("click", () => dialog.close());
  els.closeX.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close();
  });
}

export function openSummaryDialog(): void {
  if (!els) return;
  els.pre.textContent = buildSummary(stops.all());
  resetCopyButton();
  applyStaticTranslations(els.dialog);
  els.dialog.showModal();
}

function buildSummary(all: readonly Stop[]): string {
  if (all.length === 0) return i18n.t("summaryEmpty");

  const lang = i18n.current();
  const dateFmt = new Intl.DateTimeFormat(lang, {
    day: "numeric",
    month: "short",
  });

  const lines: string[] = [];
  let totalKm = 0;
  for (let i = 0; i < all.length - 1; i++) {
    totalKm += haversineKm(all[i], all[i + 1]);
  }
  const stopCountText =
    all.length === 1
      ? i18n.t("summaryStopCountOne")
      : i18n.t("summaryStopCount", { n: all.length });
  const headerParts = [`🌍 ${i18n.t("summaryHeading")}`, stopCountText];
  if (all.length >= 2) {
    headerParts.push(i18n.t("summaryTotalKm", { km: Math.round(totalKm) }));
  }
  lines.push(headerParts.join("  ·  "));
  lines.push("");

  all.forEach((stop, index) => {
    lines.push(`${index + 1}. ${stop.city || stop.name}`);
    const dateLine = formatDateRange(stop.dateFrom, stop.dateTo, dateFmt);
    if (dateLine) lines.push(`   📅 ${dateLine}`);
    if (stop.arriveBy) {
      lines.push(`   ⬇ ${TRAVEL_MODE_ICON[stop.arriveBy]} ${i18n.t(TRAVEL_LABEL_KEY[stop.arriveBy])}`);
    }
    if (stop.notes) {
      for (const noteLine of stop.notes.split("\n")) {
        lines.push(`   📝 ${noteLine}`);
      }
    }

    const next = all[index + 1];
    if (next) {
      const distanceKm = haversineKm(stop, next);
      const km = Math.round(distanceKm);
      const legMode = next.arriveBy ?? stop.departBy;
      const icon = legMode ? `${TRAVEL_MODE_ICON[legMode]}  ` : "";
      const minutes = estimateMinutes(distanceKm, legMode);
      const time = minutes ? ` · ${formatDuration(minutes)}` : "";
      lines.push("");
      lines.push(`   ${icon}${km} km${time}`);
      lines.push("");
    }
  });

  return lines.join("\n");
}

function formatDateRange(
  from: string | undefined,
  to: string | undefined,
  fmt: Intl.DateTimeFormat,
): string {
  const fromDate = parseISODate(from);
  const toDate = parseISODate(to);
  if (fromDate && toDate) {
    if (from === to) return fmt.format(fromDate);
    return `${fmt.format(fromDate)} – ${fmt.format(toDate)}`;
  }
  if (fromDate) return fmt.format(fromDate);
  if (toDate) return fmt.format(toDate);
  return "";
}

function parseISODate(value: string | undefined): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

async function copyToClipboard(): Promise<void> {
  if (!els) return;
  const text = els.pre.textContent ?? "";
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    selectText(els.pre);
    return;
  }
  flashCopied();
}

function flashCopied(): void {
  if (!els) return;
  els.copyBtn.textContent = i18n.t("summaryCopied");
  els.copyBtn.classList.add("copied");
  if (copyResetTimer) clearTimeout(copyResetTimer);
  copyResetTimer = setTimeout(resetCopyButton, 1500);
}

function resetCopyButton(): void {
  if (!els) return;
  els.copyBtn.textContent = i18n.t("summaryCopy");
  els.copyBtn.classList.remove("copied");
  copyResetTimer = null;
}

function selectText(node: Node): void {
  const range = document.createRange();
  range.selectNodeContents(node);
  const selection = window.getSelection();
  if (!selection) return;
  selection.removeAllRanges();
  selection.addRange(range);
}

function required<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Element not found: ${selector}`);
  return el;
}
